import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { BackPill } from './BackPill';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, ChevronLeft, Droplet, Scissors, CalendarRange, Plus, Sparkles } from 'lucide-react';
import LIBRARY from '../../data/Imperial_Sovereignty_Library.json';
import AegisSchedule, { REGIMEN_KEY, suggestNutritional } from './AegisSchedule';
import { useHistoryClose } from '../../hooks/useHistoryClose';
import { vibrate as nativeVibrate } from '../../utils/haptics';
import { NutrientWheel } from './aegis/NutrientWheel';
import { NutrientAtlas } from './aegis/NutrientAtlas';
import { loadAtlas, PILLAR } from './aegis/atlasData';

/* ═══════════════════════════════════════════════════════════════════
   THE AEGIS — Imperial Sovereignty Engine (v3)
   Zero-cloud-cost. 100% offline — LOCAL table only.
   Wikipedia/Wiktionary web-consult ARCHIVED (see git history) — will be
   re-introduced in a future release. All persistence in localStorage.
   ═══════════════════════════════════════════════════════════════════ */

const NAVY    = '#050507';
const NAVY_HI = '#0E0E14';
const GOLD    = '#D4AF37';
const GOLD_HI = '#FFE9A8';
const GOLD_LO = '#7A5418';

const STORAGE_KEY = 'aegis_sealed_protocols_v1'; // legacy — no longer used, kept for cleanup safety

const haptic = (ms = 12) => {
  try { nativeVibrate(ms); } catch (_) { /* unsupported */ }
};

/* ── Tile icon (used by TryLauncher) ─────────────────────────────── */
export const AegisTileIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40"
    style={{ filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.65))' }}>
    <defs>
      <linearGradient id="aegisGold" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%"  stopColor="#FFF1B8"/>
        <stop offset="50%" stopColor="#D4AF37"/>
        <stop offset="100%" stopColor="#5A3F10"/>
      </linearGradient>
      <radialGradient id="aegisCore" cx="50%" cy="45%" r="55%">
        <stop offset="0%"  stopColor="#FFE9A8"/>
        <stop offset="60%" stopColor="#8B6A22"/>
        <stop offset="100%" stopColor="#1A1004"/>
      </radialGradient>
    </defs>
    <path d="M 20 5 L 33 10 L 33 22 Q 33 32 20 36 Q 7 32 7 22 L 7 10 Z"
      fill="url(#aegisCore)" stroke="url(#aegisGold)" strokeWidth="1.5"/>
    <path d="M 20 12 L 26 15 L 26 22 Q 26 27 20 30 Q 14 27 14 22 L 14 15 Z"
      fill="none" stroke="url(#aegisGold)" strokeWidth="1" opacity="0.9"/>
    <path d="M 20 18 Q 22 22 20 26 Q 18 22 20 18 Z"
      fill="#FFE9A8" opacity="0.95"/>
  </svg>
);

/* ── Fuzzy scorer (case-insensitive, no deps) ────────────────────── */
const scoreEntry = (entry, q) => {
  if (!q) return 0;
  const query = q.trim().toLowerCase();
  if (!query) return 0;
  const kw = (entry.Keyword || '').toLowerCase();
  const aliases = (entry.Aliases || []).map((a) => a.toLowerCase());
  const chem = (entry.Chemical_Solution || '').toLowerCase();
  const nut  = (entry.Nutritional_Solution || '').toLowerCase();

  if (kw === query) return 100;
  if (aliases.some((a) => a === query)) return 95;
  if (kw.startsWith(query)) return 80;
  if (aliases.some((a) => a.startsWith(query))) return 75;
  if (kw.includes(query)) return 60;
  if (aliases.some((a) => a.includes(query))) return 55;
  if (chem.includes(query) || nut.includes(query)) return 35;
  const words = query.split(/\s+/).filter(Boolean);
  if (words.length > 1) {
    const hit = words.filter((w) => kw.includes(w) || aliases.some((a) => a.includes(w))).length;
    if (hit > 0) return 25 + hit * 4;
  }
  return 0;
};

/* ═══════════════════════════════════════════════════════════════════
   MASTER CARDS (Skin / Hair / Body Library + Regimen Schedule)
   ═══════════════════════════════════════════════════════════════════ */
const MasterCard = ({ variant, count, onOpen, accent }) => {
  const isSkinLib = variant === 'SkinLibrary';
  const isHairLib = variant === 'HairLibrary';
  const isBodyLib = variant === 'BodyLibrary';
  const isSkinReg = variant === 'SkinRegimen';
  const isHairReg = variant === 'HairRegimen';
  const isBodyReg = variant === 'BodyRegimen';
  const testid =
    isSkinLib ? 'aegis-master-skin' :
    isHairLib ? 'aegis-master-hair' :
    isBodyLib ? 'aegis-master-body' :
    isSkinReg ? 'aegis-master-schedule-skin' :
    isHairReg ? 'aegis-master-schedule-hair' :
    'aegis-master-schedule-body';
  const label =
    isSkinLib ? 'The Vessel · Library' :
    isHairLib ? 'The Mane · Library' :
    isBodyLib ? 'The Temple · Library' :
    isSkinReg ? 'The Vessel · Ritual' :
    isHairReg ? 'The Mane · Ritual' :
    'The Temple · Ritual';
  const title =
    isSkinLib ? 'Skin Library' :
    isHairLib ? 'Hair Library' :
    isBodyLib ? 'Body Library' :
    isSkinReg ? 'Skin Schedule' :
    isHairReg ? 'Hair Schedule' :
    'Body Schedule';
  const sub = (isSkinLib || isHairLib || isBodyLib)
    ? `${count} scrolls`
    : 'AM · PM · Weekly';
  const Icon = (isSkinLib || isSkinReg) ? Droplet
             : (isHairLib || isHairReg) ? Scissors
             : Sparkles;
  return (
    <button type="button" onClick={onOpen}
      className="relative w-full text-left p-3 rounded-2xl overflow-hidden transition-transform active:scale-[0.98]"
      style={{
        background: `linear-gradient(160deg, ${NAVY_HI}, ${NAVY})`,
        border: `1px solid ${accent}80`,
        boxShadow: `0 0 16px ${accent}22, inset 0 0 10px rgba(255,255,255,0.03)`,
      }}
      data-testid={testid}>
      <div className="flex items-center gap-2.5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{
            background: `radial-gradient(circle, ${accent}33, transparent 70%)`,
            border: `1px solid ${accent}AA`,
            boxShadow: `inset 0 0 10px ${accent}55`,
          }}>
          <Icon size={16} color={accent}/>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-['Outfit'] text-[8px] uppercase tracking-[0.24em] truncate" style={{ color: accent }}>
            {label}
          </p>
          <h3 className="font-['Cormorant_Garamond'] text-[15px] leading-tight truncate" style={{ color: GOLD_HI }}>
            {title}
          </h3>
          <p className="font-['Cormorant_Garamond'] italic text-[10px] mt-0.5 truncate" style={{ color: `${accent}AA` }}>
            {sub}
          </p>
        </div>
      </div>
    </button>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   ROW DETAIL (expanded inline row) — with Copy-to-Regimen
   ═══════════════════════════════════════════════════════════════════ */
const REGIMEN_SLOTS = {
  Skin: {
    'AM': [
      { g: 'am', id: 'cleanse', label: 'Cleanse' },
      { g: 'am', id: 'serum', label: 'Serum / Treatment' },
      { g: 'am', id: 'moisturizer', label: 'Moisturizer' },
      { g: 'am', id: 'sunscreen', label: 'Sunscreen' },
    ],
    'PM': [
      { g: 'pm', id: 'cleanser', label: 'Cleanser' },
      { g: 'pm', id: 'serum_pm', label: 'Serum / Treatment' },
      { g: 'pm', id: 'moisturizer_pm', label: 'Moisturizer' },
      { g: 'pm', id: 'seal', label: 'Seal' },
    ],
    'Weekly': [
      { g: 'weekly', id: 'exfoliation', label: 'Exfoliation' },
      { g: 'weekly', id: 'skin_deep_mask', label: 'Deep Hydration Mask' },
    ],
  },
  Hair: {
    'AM': [
      { g: 'am', id: 'detangle', label: 'Detangle / Style' },
      { g: 'am', id: 'scalp_stimulant', label: 'Scalp Stimulant' },
      { g: 'am', id: 'uv_shield', label: 'UV / Environmental Shield' },
    ],
    'PM': [
      { g: 'pm', id: 'scalp_cleanser', label: 'Scalp Cleanser' },
      { g: 'pm', id: 'target_treatment', label: 'Targeted Treatment' },
      { g: 'pm', id: 'repair_nourish', label: 'Repair / Nourish' },
      { g: 'pm', id: 'restoration', label: 'Restoration (Massage)' },
    ],
    'Weekly': [
      { g: 'weekly', id: 'scalp_treatment', label: 'Scalp Treatment / Mask' },
      { g: 'weekly', id: 'hair_deep_oil', label: 'Deep Hydration Oil' },
    ],
  },
  Body: {
    'AM': [
      { g: 'am', id: 'body_cleanse', label: 'Body Wash' },
      { g: 'am', id: 'body_serum',   label: 'Body Serum' },
      { g: 'am', id: 'body_moisture',label: 'Body Lotion' },
      { g: 'am', id: 'body_spf',     label: 'Body SPF' },
    ],
    'PM': [
      { g: 'pm', id: 'body_cleanse_pm', label: 'Body Wash' },
      { g: 'pm', id: 'body_treatment',  label: 'Body Treatment' },
      { g: 'pm', id: 'body_seal',       label: 'Body Cream' },
      { g: 'pm', id: 'body_oil',        label: 'Body Oil' },
    ],
    'Weekly': [
      { g: 'weekly', id: 'body_exfoliation', label: 'Body Scrub' },
      { g: 'weekly', id: 'body_brush',       label: 'Dry Brush' },
    ],
  },
};

const RowDetail = ({ entry, onCopyToSlot }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const testKey = entry.Keyword.replace(/\s+/g, '-').toLowerCase();
  const slots = REGIMEN_SLOTS[entry.Category] || {};

  const doCopy = (group, slotId) => {
    onCopyToSlot({
      category: entry.Category.toLowerCase(),
      group,
      slotId,
      chemical: entry.Chemical_Solution,
      nutritional: entry.Nutritional_Solution,
    });
    setPickerOpen(false);
  };

  return (
    <div className="px-3 py-3"
      style={{ background: 'rgba(212,175,55,0.03)', borderTop: `1px solid ${GOLD}22` }}
      data-testid={`aegis-row-detail-${testKey}`}>
      <div className="grid grid-cols-2 gap-3 text-[11px]">
        <div>
          <p className="font-['Outfit'] uppercase tracking-[0.2em] text-[9px]" style={{ color: `${GOLD}AA` }}>Protocol</p>
          <p className="font-['Cormorant_Garamond'] text-[13px] mt-0.5" style={{ color: GOLD_HI }}>
            {entry.Protocol_Label || '—'}
          </p>
        </div>
        <div>
          <p className="font-['Outfit'] uppercase tracking-[0.2em] text-[9px]" style={{ color: `${GOLD}AA` }}>Frequency</p>
          <p className="font-['Cormorant_Garamond'] text-[13px] mt-0.5" style={{ color: GOLD_HI }}>
            {entry.Frequency || '—'}
          </p>
        </div>
      </div>

      <div className="mt-3">
        <p className="font-['Outfit'] uppercase tracking-[0.2em] text-[9px]" style={{ color: `${GOLD}AA` }}>External (full)</p>
        <p className="font-['Cormorant_Garamond'] text-[13px] leading-snug mt-0.5" style={{ color: GOLD_HI }}>
          {entry.Chemical_Solution}
        </p>
      </div>
      <div className="mt-2">
        <p className="font-['Outfit'] uppercase tracking-[0.2em] text-[9px]" style={{ color: `${GOLD}AA` }}>Internal (full)</p>
        <p className="font-['Cormorant_Garamond'] text-[13px] leading-snug mt-0.5" style={{ color: GOLD_HI }}>
          {entry.Nutritional_Solution}
        </p>
      </div>
      <div className="mt-2">
        <p className="font-['Outfit'] uppercase tracking-[0.2em] text-[9px]" style={{ color: GOLD }}>Imperial Protocol</p>
        <p className="font-['Cormorant_Garamond'] text-[13px] leading-snug mt-0.5" style={{ color: `${GOLD_HI}EE` }}>
          {entry.Imperial_Protocol}
        </p>
      </div>
      <p className="mt-2 font-['Cormorant_Garamond'] italic text-[10px]" style={{ color: `${GOLD}77` }}>
        Educational reference only. Not a substitute for a dermatologist or physician. Patch-test all actives.
      </p>

      <div className="mt-3">
        <button type="button" onClick={() => setPickerOpen(true)}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-full font-['Outfit'] uppercase text-[10px] tracking-[0.22em]"
          style={{
            background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD_LO})`,
            color: '#050507',
            boxShadow: `0 0 12px ${GOLD}88`,
          }}
          data-testid={`aegis-copy-regimen-${testKey}`}>
          <Plus size={12}/> Copy to Regimen
        </button>
      </div>

      {/* Slot picker */}
      <AnimatePresence>
        {pickerOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)' }}
            onClick={() => setPickerOpen(false)}
            data-testid={`aegis-copy-picker-${testKey}`}>
            <motion.div
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md rounded-2xl p-4 max-h-[75vh] overflow-y-auto"
              style={{
                background: `linear-gradient(165deg, ${NAVY_HI}, ${NAVY})`,
                border: `1px solid ${GOLD}77`,
                boxShadow: `0 0 30px ${GOLD}44`,
              }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.28em]" style={{ color: GOLD }}>
                    Copy to Regimen
                  </p>
                  <h4 className="font-['Cormorant_Garamond'] text-lg leading-tight" style={{ color: GOLD_HI }}>
                    {entry.Keyword}
                  </h4>
                </div>
                <button type="button" onClick={() => setPickerOpen(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ border: `1px solid ${GOLD}55`, color: GOLD }}>
                  <X size={13}/>
                </button>
              </div>
              <p className="mt-2 font-['Cormorant_Garamond'] italic text-[12px]" style={{ color: `${GOLD}AA` }}>
                Pick the slot to fill with this protocol.
              </p>

              {Object.entries(slots).map(([groupLabel, list]) => (
                <div key={groupLabel} className="mt-3">
                  <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.24em]" style={{ color: `${GOLD}CC` }}>
                    {groupLabel}
                  </p>
                  <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                    {list.map((s) => (
                      <button key={`${s.g}-${s.id}`} type="button" onClick={() => doCopy(s.g, s.id)}
                        className="text-left rounded-lg px-2 py-2 font-['Outfit'] text-[11px]"
                        style={{
                          background: 'rgba(255,255,255,0.03)',
                          border: `1px solid ${GOLD}44`,
                          color: GOLD_HI,
                        }}
                        data-testid={`aegis-copy-slot-${s.g}-${s.id}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   TABLE VIEW (per-category)
   ═══════════════════════════════════════════════════════════════════ */
const truncate = (str, n) => {
  if (!str) return '';
  return str.length > n ? str.slice(0, n - 1).trimEnd() + '…' : str;
};

const TableView = ({ category, onBack, onCopyToSlot }) => {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(null); // Keyword or null
  // Google-style full-screen results — opens when user hits Enter / Search
  // button with a meaningful query. Blocks the table view underneath so the
  // user can focus on the issue · restore/elevate · reasons · remedies read-out.
  const [resultsOpen, setResultsOpen] = useState(false);
  const openResults = () => {
    if (query.trim().length >= 2) setResultsOpen(true);
  };

  const filtered = useMemo(() => {
    const base = LIBRARY.filter((e) => e.Category === category);
    if (!query.trim()) return base;
    const scored = base
      .map((e) => ({ e, score: scoreEntry(e, query) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score);
    return scored.map((r) => r.e);
  }, [query, category]);

  const toggleRow = (kw) => {
    setExpanded((prev) => prev === kw ? null : kw);
    haptic(8);
  };

  return (
    <div className="relative w-full h-full flex flex-col" data-testid={`aegis-table-${category.toLowerCase()}`}>
      {/* Sub-header inside table view */}
      <div className="px-4 pt-3 pb-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onBack}
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ border: `1px solid ${GOLD}55`, color: GOLD }}
            aria-label="Back to master cards"
            data-testid="aegis-table-back">
            <ChevronLeft size={15}/>
          </button>
          <div>
            <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.32em]" style={{ color: GOLD }}>
              {category === 'Skin' ? 'The Vessel'
                : category === 'Hair' ? 'The Mane'
                : 'The Temple'}
            </p>
            <h3 className="font-['Cormorant_Garamond'] text-xl leading-tight" style={{ color: GOLD_HI }}>
              {category} · Sovereignty Table
            </h3>
          </div>
        </div>

        {/* Search bar inside the table view */}
        <div className="mt-3 flex items-center gap-2 rounded-full px-4 py-2"
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${GOLD}55`,
            boxShadow: `inset 0 0 8px rgba(255,255,255,0.02)`,
          }}>
          <Search size={14} color={GOLD}/>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') openResults(); }}
            placeholder={`Search ${category.toLowerCase()} concerns or goals…`}
            className="flex-1 bg-transparent outline-none font-['Outfit'] text-[13px]"
            style={{ color: GOLD_HI }}
            maxLength={80}
            data-testid={`aegis-table-search-${category.toLowerCase()}`}
          />
          {query && (
            <button type="button" onClick={() => setQuery('')}
              className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{ color: `${GOLD}CC` }}
              aria-label="Clear search"
              data-testid="aegis-table-clear-search">
              <X size={11}/>
            </button>
          )}
          <button type="button" onClick={openResults}
            disabled={query.trim().length < 2}
            className="px-3 py-1 rounded-full font-['Outfit'] text-[10px] uppercase tracking-[0.22em]"
            style={{
              background: query.trim().length >= 2
                ? `linear-gradient(135deg, ${GOLD_HI}, ${GOLD})`
                : 'rgba(255,255,255,0.04)',
              color: query.trim().length >= 2 ? '#050507' : `${GOLD}66`,
              border: `1px solid ${GOLD}${query.trim().length >= 2 ? 'CC' : '44'}`,
            }}
            aria-label="Search this concern"
            data-testid="aegis-table-search-go">
            Search
          </button>
        </div>
      </div>

      {/* Table (fits window, 3 primary cols) */}
      <div className="flex-1 overflow-y-auto px-3 pb-8" data-testid={`aegis-table-body-${category.toLowerCase()}`}>
        <div className="rounded-xl overflow-hidden"
          style={{ border: `1px solid ${GOLD}44`, background: NAVY_HI }}>
          {/* Header row */}
          <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)_minmax(0,1.4fr)] gap-1 px-2 py-2"
            style={{ background: `${GOLD}12`, borderBottom: `1px solid ${GOLD}44` }}>
            <div className="font-['Outfit'] text-[9px] uppercase tracking-[0.18em]" style={{ color: GOLD_HI }}>
              {category === 'Skin' ? 'Concern' : 'Concern'}
            </div>
            <div className="font-['Outfit'] text-[9px] uppercase tracking-[0.18em]" style={{ color: GOLD_HI }}>
              External
            </div>
            <div className="font-['Outfit'] text-[9px] uppercase tracking-[0.18em]" style={{ color: GOLD_HI }}>
              Internal
            </div>
          </div>

          {/* Rows */}
          {filtered.length === 0 ? (
            <div className="p-6 text-center">
              <p className="font-['Cormorant_Garamond'] text-base" style={{ color: GOLD_HI }}>
                No local scrolls match “{query}”.
              </p>
              <p className="font-['Cormorant_Garamond'] italic text-xs mt-1" style={{ color: `${GOLD}88` }}>
                Consulting the wider archives…
              </p>
            </div>
          ) : (
            filtered.map((entry) => {
              const testKey = entry.Keyword.replace(/\s+/g, '-').toLowerCase();
              const isOpen = expanded === entry.Keyword;
              const isProblem = entry.Type === 'Problem';
              const rowTint = isProblem ? 'rgba(255,120,120,0.05)' : 'rgba(212,175,55,0.05)';
              const rowLine = isProblem ? '#B22222' : GOLD_HI;
              return (
                <div key={entry.Keyword}
                  style={{ borderTop: `1px solid ${GOLD}22`, background: isOpen ? `${GOLD}0F` : rowTint }}
                  data-testid={`aegis-row-${testKey}`}
                  data-type={isProblem ? 'restore' : 'elevate'}>
                  <button type="button" onClick={() => toggleRow(entry.Keyword)}
                    className="w-full grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)_minmax(0,1.4fr)] gap-1 px-2 py-2 text-left transition-colors relative"
                    aria-expanded={isOpen}
                    data-testid={`aegis-row-toggle-${testKey}`}>
                    <span aria-hidden className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r"
                      style={{ background: rowLine, opacity: 0.85 }}/>
                    <div className="min-w-0 pl-1.5">
                      <p className="font-['Cormorant_Garamond'] text-[13px] leading-tight" style={{ color: GOLD_HI }}>
                        {entry.Keyword}
                      </p>
                      <p className="font-['Outfit'] text-[8px] uppercase tracking-[0.16em] mt-0.5"
                        style={{ color: isProblem ? '#FF9090' : GOLD_HI }}>
                        {isProblem ? '● Restore' : '✦ Elevate'}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="font-['Outfit'] text-[11px] leading-snug" style={{ color: `${GOLD_HI}CC` }}>
                        {truncate(entry.Chemical_Solution, 44)}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="font-['Outfit'] text-[11px] leading-snug" style={{ color: `${GOLD_HI}CC` }}>
                        {truncate(entry.Nutritional_Solution, 44)}
                      </p>
                    </div>
                  </button>
                  {isOpen && (
                    <RowDetail entry={entry} onCopyToSlot={onCopyToSlot}/>
                  )}
                </div>
              );
            })
          )}
        </div>

      </div>

      {/* ── Full-screen LOCAL Results panel ────────────────────────────
          Opens when the user hits Enter or the Search button with a
          meaningful query. Presents ALL matching local scrolls with
          Restore/Elevate tag, external + internal solution, protocol
          label & frequency, and the full Imperial Protocol read-out. */}
      <AnimatePresence>
        {resultsOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[160] flex flex-col imperium-shell"
            style={{ background: '#020203' }}
            data-testid="aegis-results-modal">
            {/* Modal header */}
            <div className="flex-shrink-0 flex items-center gap-2 px-4 py-3 backdrop-blur-md"
              style={{ background: `${NAVY}E6`, borderBottom: `1px solid ${GOLD}33` }}>
              <button type="button" onClick={() => setResultsOpen(false)}
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ border: `1px solid ${GOLD}55`, color: GOLD }}
                aria-label="Back to table"
                data-testid="aegis-results-back">
                <ChevronLeft size={15}/>
              </button>
              <div className="flex-1 min-w-0">
                <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.28em]" style={{ color: GOLD }}>
                  {category} · Search · {filtered.length} result{filtered.length === 1 ? '' : 's'}
                </p>
                <p className="font-['Cormorant_Garamond'] text-[16px] leading-tight truncate" style={{ color: GOLD_HI }}>
                  {query}
                </p>
              </div>
            </div>

            {/* Body — local matches */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {filtered.length === 0 ? (
                <div className="rounded-xl p-5"
                  style={{ background: NAVY_HI, border: `1px solid ${GOLD}44` }}
                  data-testid="aegis-results-empty">
                  <p className="font-['Cormorant_Garamond'] text-[17px]" style={{ color: GOLD_HI }}>
                    No local scrolls match “{query}”.
                  </p>
                  <p className="font-['Cormorant_Garamond'] italic text-[13px] mt-2" style={{ color: `${GOLD}99` }}>
                    Try a shorter term or a related concern — the archives cover
                    common {category.toLowerCase()} care topics only.
                  </p>
                </div>
              ) : (
                <>
                  {filtered.map((entry) => {
                    const isProblem = entry.Type === 'Problem';
                    const tagColour = isProblem ? '#FF9090' : GOLD_HI;
                    const tagBg     = isProblem ? 'rgba(255,120,120,0.10)' : 'rgba(212,175,55,0.10)';
                    const tagLabel  = isProblem ? '● Restore' : '✦ Elevate';
                    const testKey   = entry.Keyword.replace(/\s+/g, '-').toLowerCase();
                    return (
                      <div key={entry.Keyword}
                        className="rounded-xl p-4"
                        style={{ background: NAVY_HI, border: `1px solid ${GOLD}55` }}
                        data-testid={`aegis-results-card-${testKey}`}>
                        {/* Header row: keyword + Restore/Elevate tag */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.22em]" style={{ color: `${GOLD}AA` }}>
                              The Issue
                            </p>
                            <h4 className="font-['Cormorant_Garamond'] text-[19px] leading-tight" style={{ color: GOLD_HI }}>
                              {entry.Keyword}
                            </h4>
                            {entry.Aliases?.length > 0 && (
                              <p className="font-['Cormorant_Garamond'] italic text-[11px] leading-snug mt-0.5"
                                style={{ color: `${GOLD}88` }}>
                                also: {entry.Aliases.slice(0, 4).join(', ')}
                              </p>
                            )}
                          </div>
                          <span className="flex-shrink-0 px-2 py-1 rounded-full font-['Outfit'] uppercase text-[9px] tracking-[0.2em]"
                            style={{ background: tagBg, color: tagColour, border: `1px solid ${tagColour}55` }}>
                            {tagLabel}
                          </span>
                        </div>

                        {/* Protocol / Frequency mini-grid */}
                        <div className="mt-3 grid grid-cols-2 gap-3">
                          <div>
                            <p className="font-['Outfit'] uppercase tracking-[0.2em] text-[9px]" style={{ color: `${GOLD}AA` }}>Protocol</p>
                            <p className="font-['Cormorant_Garamond'] text-[13px] mt-0.5" style={{ color: GOLD_HI }}>
                              {entry.Protocol_Label || '—'}
                            </p>
                          </div>
                          <div>
                            <p className="font-['Outfit'] uppercase tracking-[0.2em] text-[9px]" style={{ color: `${GOLD}AA` }}>Frequency</p>
                            <p className="font-['Cormorant_Garamond'] text-[13px] mt-0.5" style={{ color: GOLD_HI }}>
                              {entry.Frequency || '—'}
                            </p>
                          </div>
                        </div>

                        {/* External (Chemical) remedy */}
                        <div className="mt-3">
                          <p className="font-['Outfit'] uppercase tracking-[0.2em] text-[9px]" style={{ color: `${GOLD}AA` }}>
                            External · Cure
                          </p>
                          <p className="font-['Cormorant_Garamond'] text-[13px] leading-snug mt-0.5" style={{ color: GOLD_HI }}>
                            {entry.Chemical_Solution}
                          </p>
                        </div>

                        {/* Internal (Nutritional) remedy */}
                        <div className="mt-2">
                          <p className="font-['Outfit'] uppercase tracking-[0.2em] text-[9px]" style={{ color: `${GOLD}AA` }}>
                            Internal · Nourish
                          </p>
                          <p className="font-['Cormorant_Garamond'] text-[13px] leading-snug mt-0.5" style={{ color: GOLD_HI }}>
                            {entry.Nutritional_Solution}
                          </p>
                        </div>

                        {/* Imperial Protocol (full read) */}
                        <div className="mt-2">
                          <p className="font-['Outfit'] uppercase tracking-[0.2em] text-[9px]" style={{ color: GOLD }}>
                            Imperial Protocol · Reason &amp; Practice
                          </p>
                          <p className="font-['Cormorant_Garamond'] text-[13px] leading-snug mt-0.5" style={{ color: `${GOLD_HI}EE` }}>
                            {entry.Imperial_Protocol}
                          </p>
                        </div>
                      </div>
                    );
                  })}

                  {/* Disclaimer */}
                  <div className="rounded-xl p-4"
                    style={{ background: 'rgba(212,175,55,0.04)', border: `1px solid ${GOLD}55` }}>
                    <p className="font-['Cormorant_Garamond'] italic text-[12px] leading-relaxed"
                      style={{ color: `${GOLD}AA` }}
                      data-testid="aegis-results-disclaimer">
                      Educational reference only — not medical advice. For
                      persistent, severe or worsening symptoms, consult a licensed
                      dermatologist, trichologist or physician. Patch-test all actives.
                    </p>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   ROOT — TheAegis
   ═══════════════════════════════════════════════════════════════════ */
export const TheAegis = ({ onClose }) => {
  const [view, setView] = useState('master'); // 'master' | 'skin' | 'hair' | 'schedule-skin' | 'schedule-hair'
  const [copyToast, setCopyToast] = useState(null); // { label } shown briefly after copy

  // Hardware/gesture back: walk sub-views back to master, then close.
  useHistoryClose(true, useCallback(() => {
    if (view !== 'master') {
      setView('master');
      // Re-push a sentinel so the next back press also lands in this hook.
      try { window.history.pushState({ __modal: Date.now() }, ''); } catch (_) { /* ignore */ }
    } else {
      onClose();
    }
  }, [view, onClose]));

  // One-time cleanup of legacy sealed-protocols storage.
  useEffect(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
  }, []);

  const skinCount = useMemo(() => LIBRARY.filter((e) => e.Category === 'Skin').length, []);
  const hairCount = useMemo(() => LIBRARY.filter((e) => e.Category === 'Hair').length, []);
  const bodyCount = useMemo(() => LIBRARY.filter((e) => e.Category === 'Body').length, []);
  const [foodCount, setFoodCount] = useState(0);
  useEffect(() => { loadAtlas().then((a) => setFoodCount(a.foods.length)).catch(() => {}); }, []);

  // Copy a library entry into a specific regimen slot (localStorage direct write).
  const handleCopyToSlot = useCallback(({ category, group, slotId, chemical, nutritional }) => {
    try {
      const raw = localStorage.getItem(REGIMEN_KEY);
      const regimen = raw ? JSON.parse(raw) : {};
      const cat = regimen[category] || {};
      const list = Array.isArray(cat[group]) ? cat[group] : [];
      const idx = list.findIndex((s) => s.id === slotId);
      const nut = nutritional
        || (chemical ? (suggestNutritional(chemical, category)?.nutritional || '') : '');
      if (idx >= 0) {
        list[idx] = { ...list[idx], product: chemical, nutrition: nut };
      } else {
        list.push({ id: slotId, product: chemical, nutrition: nut });
      }
      const nextCat = { ...cat, [group]: list };
      const next = { ...regimen, [category]: nextCat };
      localStorage.setItem(REGIMEN_KEY, JSON.stringify(next));
      haptic(25);
      const catLabel = category === 'skin' ? 'Skin'
                     : category === 'hair' ? 'Hair'
                     : 'Body';
      setCopyToast({ label: `Copied → ${catLabel} · ${group.toUpperCase()}` });
      setTimeout(() => setCopyToast(null), 1800);
    } catch (_) { /* storage error */ }
  }, []);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[110] overflow-hidden flex flex-col imperium-shell"
      style={{ background: '#020203' }}
      data-testid="aegis-modal">

      {/* Header */}
      <div className="relative z-20 flex-shrink-0 backdrop-blur-md"
        style={{ background: `linear-gradient(180deg, ${NAVY}E6, ${NAVY}99)`, borderBottom: `1px solid ${GOLD}33` }}>
        <div className="flex items-center justify-between pl-4 pr-14 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <BackPill onClick={onClose} accent="#FFB265" testId="aegis-close"/>
            <AegisTileIcon size={22}/>
            <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.32em] truncate" style={{ color: GOLD }}>
              The Aegis
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="relative flex-1 overflow-hidden flex flex-col">
        {view === 'master' && (
          <div className="flex-1 min-h-0 flex flex-col px-4 pt-3" data-testid="aegis-master-view"
            style={{ paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 12px)' }}>
            <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.32em]" style={{ color: GOLD }}>
              Imperial Sovereignty Engine
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-xl leading-tight mt-0.5" style={{ color: GOLD_HI }}>
              Restore. Elevate. Reign.
            </h2>
            <p className="font-['Cormorant_Garamond'] italic text-[12px] mt-0.5" style={{ color: `${GOLD}88` }}>
              Choose the domain of the vessel that carries the throne.
            </p>

            {/* 2-column grid — six tiles fit compactly with the bottom fully visible. */}
            <div className="mt-3 grid grid-cols-2 gap-2.5 flex-shrink-0">
              <MasterCard variant="SkinLibrary" count={skinCount} accent={PILLAR.fuel} onOpen={() => setView('skin')}/>
              <MasterCard variant="SkinRegimen" accent={PILLAR.force} onOpen={() => setView('schedule-skin')}/>
              <MasterCard variant="HairLibrary" count={hairCount} accent={PILLAR.power} onOpen={() => setView('hair')}/>
              <MasterCard variant="HairRegimen" accent={PILLAR.energy} onOpen={() => setView('schedule-hair')}/>
              <MasterCard variant="BodyLibrary" count={bodyCount} accent={PILLAR.aegis} onOpen={() => setView('body')}/>
              <MasterCard variant="BodyRegimen" accent={PILLAR.destiny} onOpen={() => setView('schedule-body')}/>
            </div>
            <div className="flex-1 min-h-[120px] pt-3 flex items-center justify-center overflow-hidden">
              <NutrientWheel count={foodCount} onOpen={() => setView('nutrients')}/>
            </div>
          </div>
        )}
        {view === 'nutrients' && <NutrientAtlas onBack={() => setView('master')}/>}
        {view === 'skin' && (
          <TableView category="Skin" onBack={() => setView('master')} onCopyToSlot={handleCopyToSlot}/>
        )}
        {view === 'hair' && (
          <TableView category="Hair" onBack={() => setView('master')} onCopyToSlot={handleCopyToSlot}/>
        )}
        {view === 'body' && (
          <TableView category="Body" onBack={() => setView('master')} onCopyToSlot={handleCopyToSlot}/>
        )}
        {view === 'schedule-skin' && (
          <AegisSchedule category="skin" onBack={() => setView('master')}/>
        )}
        {view === 'schedule-hair' && (
          <AegisSchedule category="hair" onBack={() => setView('master')}/>
        )}
        {view === 'schedule-body' && (
          <AegisSchedule category="body" onBack={() => setView('master')}/>
        )}
      </div>

      {/* Copy toast */}
      <AnimatePresence>
        {copyToast && (
          <motion.div
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] px-4 py-2.5 rounded-full font-['Outfit'] text-[11px] uppercase tracking-[0.22em]"
            style={{
              background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD_LO})`,
              color: '#050507',
              boxShadow: `0 0 18px ${GOLD}AA`,
            }}
            data-testid="aegis-copy-toast">
            {copyToast.label}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default TheAegis;
